import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { CheckCircle2, Lock, Loader2, ChevronRight, User, Users, ArrowUpRight, Sparkles, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Brand tokens (light theme) ----
const SKY = '#35B7E8';
const SKY_DEEP = '#1F8FBE';
const NAVY = '#0B1437';
const NAVY_SOFT = '#2B3674';
const MUTED = '#7B8597';
const FAINT = '#A3AED0';
const LINE = '#E5EBF5';
const GLASS = 'rgba(255,255,255,0.65)';

const fmtUSD = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;
const fmtUSDShort = (n) => {
  const x = Number(n || 0);
  if (x >= 1000) return `$${(x / 1000).toFixed(x >= 10000 ? 0 : 1)}k`;
  return `$${Math.round(x)}`;
};

// ---- Circular badge with optional gradient fill. Accepts numeric `pct` or
// custom `text` (e.g. "$20k") for the bonus tiers. ----
const LevelBadge = ({ pct, text, variant = 'default', size = 64 }) => {
  // variant: current | achieved | next | locked | default
  const styles = {
    current: {
      bg: `linear-gradient(135deg, ${SKY} 0%, #4FC3F7 50%, ${SKY_DEEP} 100%)`,
      border: 'rgba(255,255,255,0.4)',
      text: 'white',
      shadow: `0 12px 40px ${SKY}55, 0 4px 12px ${SKY}30, inset 0 1px 0 rgba(255,255,255,0.5)`,
    },
    achieved: {
      bg: `linear-gradient(135deg, #DCEEF8 0%, #B7DEF1 100%)`,
      border: '#B7DEF1',
      text: SKY_DEEP,
      shadow: `0 4px 12px ${SKY}15`,
    },
    next: {
      bg: 'white',
      border: SKY,
      text: SKY_DEEP,
      shadow: `0 4px 16px ${SKY}25, inset 0 0 0 1px rgba(255,255,255,0.8)`,
    },
    locked: {
      bg: '#F4F7FE',
      border: LINE,
      text: FAINT,
      shadow: 'none',
    },
    default: {
      bg: '#F4F7FE',
      border: LINE,
      text: MUTED,
      shadow: 'none',
    },
  };
  const s = styles[variant] || styles.default;
  return (
    <div
      className="relative flex shrink-0 items-center justify-center rounded-full"
      style={{
        width: size, height: size,
        background: s.bg,
        border: `1.5px solid ${s.border}`,
        boxShadow: s.shadow,
      }}
    >
      <span
        className="font-bold tabular-nums"
        style={{ color: s.text, fontSize: text ? size * 0.26 : size * 0.32, fontFamily: '"Manrope", system-ui, sans-serif', letterSpacing: '-0.02em' }}
      >
        {text != null ? text : `${pct}%`}
      </span>
    </div>
  );
};

const ProgressBar = ({ value, gradient, height = 6 }) => (
  <div className="w-full overflow-hidden rounded-full" style={{ background: '#EEF2F9', height }}>
    <motion.div
      className="h-full rounded-full"
      initial={{ width: 0 }}
      animate={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      style={{ background: gradient || `linear-gradient(90deg, ${SKY_DEEP}, ${SKY})`, boxShadow: value > 0 ? `0 0 12px ${SKY}40` : 'none' }}
    />
  </div>
);

export default function Promotions() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [bonusData, setBonusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('promotions'); // 'promotions' | 'bonuses'

  const fetchData = useCallback(async () => {
    try {
      const [promoRes, bonusRes] = await Promise.all([
        axios.get(`${API}/promotions/me`, getAuthHeader()),
        axios.get(`${API}/bonuses/me`, getAuthHeader()),
      ]);
      setData(promoRes.data);
      setBonusData(bonusRes.data);
    } catch (e) {
      toast.error('Failed to load promotions');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: SKY }} />
      </div>
    );
  }
  if (!data) return null;

  const { agent, ladder, current_month, next_rung, target_eval, also_qualified_levels, days_remaining_this_month } = data;
  const ladderRendered = [...ladder].reverse();

  const currentPersonalAP = current_month ? current_month.personal_ap : 0;
  const currentTeamAP = current_month ? current_month.team_ap_qualifying : 0;
  const pThr = next_rung?.personal_threshold;
  const tThr = next_rung?.team_threshold;

  return (
    <div className="relative -m-4 sm:-m-6 lg:-m-8 min-h-screen overflow-hidden" data-testid="promotions-page"
      style={{ background: 'linear-gradient(180deg, #F7FAFE 0%, #EEF3FB 60%, #E8EFFA 100%)' }}
    >
      {/* Decorative gradient orbs */}
      <div className="pointer-events-none absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full opacity-60 blur-3xl"
        style={{ background: `radial-gradient(circle, ${SKY}55 0%, transparent 70%)` }} />
      <div className="pointer-events-none absolute top-1/3 -right-40 h-[500px] w-[500px] rounded-full opacity-40 blur-3xl"
        style={{ background: `radial-gradient(circle, #A78BFA45 0%, transparent 70%)` }} />
      <div className="pointer-events-none absolute -bottom-20 left-1/3 h-[400px] w-[400px] rounded-full opacity-50 blur-3xl"
        style={{ background: `radial-gradient(circle, ${SKY}35 0%, transparent 70%)` }} />

      <div className="relative max-w-[1100px] mx-auto px-4 py-8 sm:px-8 sm:py-12 lg:px-10 lg:py-14">

        {/* ---------- Header + tab toggle ---------- */}
        <div className="mb-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: SKY_DEEP }}>Promotions & Bonuses</div>
          <h1 className="mt-1 font-['Manrope'] text-3xl font-extrabold tracking-tight sm:text-4xl" style={{ color: NAVY }}>
            Your climb
          </h1>
        </div>

        {/* Tab pill */}
        <div
          className="mb-8 inline-flex items-center gap-1 rounded-full p-1"
          style={{
            background: 'rgba(255,255,255,0.65)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: '1px solid rgba(255,255,255,0.8)',
            boxShadow: '0 4px 12px rgba(11,20,55,0.06), inset 0 1px 0 rgba(255,255,255,0.6)',
          }}
          data-testid="promo-tabs"
        >
          {[
            { key: 'promotions', label: 'Promotions' },
            { key: 'bonuses', label: 'Bonuses' },
          ].map((t) => {
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                data-testid={`promo-tab-${t.key}`}
                className="rounded-full px-5 py-2 text-xs font-bold uppercase tracking-[0.15em] transition-all"
                style={{
                  background: active ? `linear-gradient(135deg, ${SKY_DEEP}, ${SKY})` : 'transparent',
                  color: active ? 'white' : NAVY_SOFT,
                  boxShadow: active ? `0 6px 16px ${SKY}45` : 'none',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* ============================================================
              SECTION 1 — PROMOTIONS LADDER (visible when tab = promotions)
            ============================================================ */}
        {activeTab === 'promotions' && (
        <>

        {/* ========== CURRENT STATUS (glass card) ========== */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-[28px]"
          style={{
            background: GLASS,
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(255,255,255,0.8)',
            boxShadow: '0 20px 60px rgba(11,20,55,0.08), 0 4px 16px rgba(11,20,55,0.04), inset 0 1px 0 rgba(255,255,255,0.6)',
          }}
          data-testid="promo-current-card"
        >
          {/* Top half — current level (the focal point) */}
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-5">
              <LevelBadge pct={agent.current_level_pct} variant="current" size={92} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: SKY_DEEP }}>
                  <Sparkles className="h-3 w-3" /> You are here
                </div>
                <div className="mt-1 font-['Manrope'] text-2xl font-extrabold tracking-tight sm:text-3xl" style={{ color: NAVY }}>
                  {agent.current_title || `${agent.current_level_pct}% contract`}
                </div>
                <div className="mt-1 text-sm" style={{ color: MUTED }}>{agent.name}</div>
              </div>
            </div>
          </div>

          {/* Up-next strip — subtler */}
          {next_rung && (
            <div
              className="border-t"
              style={{
                borderColor: 'rgba(229,235,245,0.7)',
                background: 'linear-gradient(180deg, rgba(247,250,254,0.4) 0%, rgba(238,242,249,0.6) 100%)',
              }}
            >
              <div className="p-6 sm:p-8">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full" style={{ background: `${SKY}15` }}>
                      <ArrowUpRight className="h-3.5 w-3.5" style={{ color: SKY_DEEP }} />
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: FAINT }}>Up next</div>
                      <div className="mt-0.5 text-sm font-semibold" style={{ color: NAVY }}>
                        {next_rung.title} <span style={{ color: MUTED }}>· {next_rung.level_pct}%</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-[11px]" style={{ color: MUTED }}>
                    {target_eval?.qualified ? (
                      <span className="font-semibold" style={{ color: '#16A34A' }}>Qualified — pending</span>
                    ) : (
                      <span>{days_remaining_this_month} day{days_remaining_this_month === 1 ? '' : 's'} left this Breeze month</span>
                    )}
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <PathRow
                    kind="personal" icon={User} label="Personal AP"
                    current={currentPersonalAP} threshold={pThr}
                    streak={target_eval?.personal_streak || 0}
                    qualMonths={next_rung.qual_months}
                    qualified={target_eval?.personal_qualified}
                  />
                  <PathRow
                    kind="team" icon={Users} label="Team AP"
                    current={currentTeamAP} threshold={tThr}
                    streak={target_eval?.team_streak || 0}
                    qualMonths={next_rung.qual_months}
                    qualified={target_eval?.team_qualified}
                    capNote={current_month?.largest_leg_capped_from
                      ? `Largest leg capped at ${fmtUSD(current_month.largest_leg_capped_to)} (was ${fmtUSD(current_month.largest_leg_capped_from)})`
                      : null}
                  />
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* ========== FULL LADDER ========== */}
        <div className="mt-6">
          <div className="mb-5">
            <div className="mt-1 text-sm" style={{ color: MUTED }}>Hit either path for the listed months in a row to promote.</div>
          </div>

          <div className="space-y-2">
            {ladderRendered.map((rung, idx) => {
              const isCurrent = rung.level_pct === agent.current_level_pct;
              const isAchieved = rung.level_pct < agent.current_level_pct;
              const isNext = next_rung && rung.level_pct === next_rung.level_pct;
              const isAlsoQualified = also_qualified_levels?.includes(rung.level_pct) && !isNext;
              const isLocked = !isCurrent && !isAchieved && !isNext && !isAlsoQualified;

              const prevRung = ladderRendered[idx - 1];
              const showGroup = rung.group_label && (!prevRung || prevRung.group_label !== rung.group_label);

              let variant = 'locked';
              if (isCurrent) variant = 'current';
              else if (isAchieved) variant = 'achieved';
              else if (isNext) variant = 'next';

              // Card styling based on state
              let cardStyle = {
                background: 'rgba(255,255,255,0.5)',
                border: `1px solid rgba(229,235,245,0.7)`,
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
              };
              if (isCurrent) {
                cardStyle = {
                  background: `linear-gradient(135deg, rgba(255,255,255,0.85) 0%, ${SKY}10 100%)`,
                  border: `1px solid ${SKY}50`,
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  boxShadow: `0 8px 24px ${SKY}20, inset 0 1px 0 rgba(255,255,255,0.6)`,
                };
              } else if (isNext) {
                cardStyle = {
                  background: 'rgba(255,255,255,0.7)',
                  border: `1px solid ${SKY}40`,
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  boxShadow: `0 4px 12px rgba(11,20,55,0.04)`,
                };
              }

              return (
                <div key={rung.level_pct}>
                  {showGroup && (
                    <div className="px-1 pb-1.5 pt-5 text-[9px] font-bold uppercase tracking-[0.3em]" style={{ color: SKY_DEEP }}>
                      {rung.group_label}
                    </div>
                  )}
                  <motion.div
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.015 }}
                    className="flex items-center gap-4 rounded-2xl px-4 py-3 transition-all hover:shadow-md"
                    style={{ ...cardStyle, opacity: isLocked ? 0.7 : 1 }}
                    data-testid={`promo-rung-${rung.level_pct}`}
                  >
                    <LevelBadge pct={rung.level_pct} variant={variant} size={48} />

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span
                          className="font-bold tracking-tight"
                          style={{
                            color: isLocked ? MUTED : NAVY,
                            fontSize: 'clamp(13px, 1.4vw, 15px)',
                          }}
                        >
                          {rung.title}
                        </span>
                        {isCurrent && (
                          <span
                            className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white"
                            style={{ background: `linear-gradient(90deg, ${SKY_DEEP}, ${SKY})` }}
                            data-testid="promo-you-are-here"
                          >
                            You
                          </span>
                        )}
                        {isAlsoQualified && (
                          <span className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest" style={{ background: '#E8F8EE', color: '#16A34A', border: '1px solid #BBE5C9' }}>
                            Ready
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px]" style={{ color: isLocked ? FAINT : MUTED }}>
                        {rung.personal_threshold != null ? (
                          <span className="tabular-nums">
                            {rung.personal_threshold === 0 ? 'Entry' : `${fmtUSDShort(rung.personal_threshold)} personal`}
                          </span>
                        ) : null}
                        {rung.personal_threshold != null && rung.team_threshold != null && <span style={{ color: FAINT }}>·</span>}
                        {rung.team_threshold != null && (
                          <span className="tabular-nums">{fmtUSDShort(rung.team_threshold)} team</span>
                        )}
                        {rung.qual_months > 0 && (
                          <>
                            <span style={{ color: FAINT }}>·</span>
                            <span>{rung.qual_months} mo{rung.qual_months > 1 ? 's' : ''}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="shrink-0">
                      {isAchieved ? <CheckCircle2 className="h-4 w-4" style={{ color: SKY_DEEP }} /> :
                       isLocked ? <Lock className="h-3.5 w-3.5" style={{ color: FAINT }} /> :
                       isNext ? <ChevronRight className="h-4 w-4" style={{ color: SKY }} /> : null}
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center text-[10px]" style={{ color: FAINT }}>
            AP = Issued + Paid · Full credit on life policies · 5% on annuities · Team AP applies the 50% max rule
          </div>
        </div>
        </>
        )}

        {/* ============================================================
              SECTION 2 — MONTHLY PRODUCER BONUS (visible when tab = bonuses)
            ============================================================ */}
        {activeTab === 'bonuses' && bonusData && <BonusesSection bonusData={bonusData} />}
      </div>
    </div>
  );
}

// ---- Slim path row ----
function PathRow({ kind, icon: Icon, label, current, threshold, streak, qualMonths, qualified, capNote }) {
  if (threshold == null) {
    return (
      <div className="rounded-xl p-3 text-center text-[11px]" style={{ background: '#F4F7FE', color: FAINT }}>
        No {kind} path on this rung
      </div>
    );
  }
  const pct = Math.min(100, (current / threshold) * 100);
  const remaining = Math.max(0, threshold - current);

  return (
    <div
      className="space-y-2 rounded-2xl p-3.5"
      style={{
        background: 'rgba(255,255,255,0.7)',
        border: '1px solid rgba(255,255,255,0.9)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7), 0 1px 3px rgba(11,20,55,0.04)',
      }}
      data-testid={`path-${kind}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5" style={{ color: SKY_DEEP }} />
          <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: NAVY_SOFT }}>{label}</span>
        </div>
        <span className="text-[10px] font-semibold tabular-nums" style={{ color: qualified ? '#16A34A' : FAINT }}>
          {qualified ? 'Qualified' : `${streak}/${qualMonths} mo`}
        </span>
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-['Manrope'] text-xl font-extrabold tabular-nums" style={{ color: NAVY }} data-testid={`path-${kind}-ap`}>
          {fmtUSD(current)}
        </span>
        <span className="text-[10px] tabular-nums" style={{ color: MUTED }}>
          / {fmtUSD(threshold)} {remaining > 0 && <span style={{ color: FAINT }}>· {fmtUSD(remaining)} to go</span>}
        </span>
      </div>
      <ProgressBar value={pct} gradient={qualified ? 'linear-gradient(90deg, #16A34A, #4ADE80)' : `linear-gradient(90deg, ${SKY_DEEP}, ${SKY})`} />
      {capNote && (
        <div className="text-[10px]" style={{ color: '#B45309' }}>{capNote}</div>
      )}
    </div>
  );
}



// ============================================================
// BONUSES SECTION — mirrors the Promotions visual language
// ============================================================
function BonusesSection({ bonusData }) {
  const {
    tiers, current_month, net_issued_life_ap,
    current_tier, current_bonus, next_tier, gap_to_next, marginal_cash,
    is_maxed, below_floor, recent_months,
  } = bonusData;

  const ladderRendered = [...tiers].reverse();
  const ap = net_issued_life_ap || 0;
  const daysLeft = current_month?.days_remaining ?? 0;

  // Hero title text — what's currently locked in
  const heroBadgeText = current_tier ? fmtUSDShort(current_tier.tier_ap) : '$0';
  const heroTitle = current_bonus > 0 ? `${fmtUSD(current_bonus)} locked in` : 'Not qualified yet';
  const heroSub = current_tier
    ? `Hit the ${fmtUSDShort(current_tier.tier_ap)} tier this Breeze month`
    : 'Reach $20,000 net life AP to unlock your first bonus';

  return (
    <>
      {/* ---------- Current status glass card ---------- */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-[28px]"
        style={{
          background: GLASS,
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.8)',
          boxShadow: '0 20px 60px rgba(11,20,55,0.08), 0 4px 16px rgba(11,20,55,0.04), inset 0 1px 0 rgba(255,255,255,0.6)',
        }}
        data-testid="bonus-current-card"
      >
        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-5">
            <LevelBadge text={heroBadgeText} variant={current_bonus > 0 ? 'current' : 'locked'} size={92} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: SKY_DEEP }}>
                <Sparkles className="h-3 w-3" /> Your bonus this month
              </div>
              <div className="mt-1 font-['Manrope'] text-2xl font-extrabold tracking-tight sm:text-3xl" style={{ color: NAVY }}>
                {heroTitle}
              </div>
              <div className="mt-1 text-sm" style={{ color: MUTED }}>{heroSub}</div>
            </div>
          </div>
        </div>

        {/* Up-next strip — same pattern as Promotions */}
        {!is_maxed && next_tier && (
          <div
            className="border-t"
            style={{
              borderColor: 'rgba(229,235,245,0.7)',
              background: 'linear-gradient(180deg, rgba(247,250,254,0.4) 0%, rgba(238,242,249,0.6) 100%)',
            }}
          >
            <div className="p-6 sm:p-8">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full" style={{ background: `${SKY}15` }}>
                    <ArrowUpRight className="h-3.5 w-3.5" style={{ color: SKY_DEEP }} />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: FAINT }}>Up next</div>
                    <div className="mt-0.5 text-sm font-semibold" style={{ color: NAVY }}>
                      {fmtUSD(next_tier.cash_bonus)} bonus <span style={{ color: MUTED }}>· {fmtUSDShort(next_tier.tier_ap)} AP tier</span>
                    </div>
                  </div>
                </div>
                <div className="text-[11px]" style={{ color: MUTED }}>
                  {daysLeft} day{daysLeft === 1 ? '' : 's'} left this Breeze month
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <PathRow
                  kind="bonus-ap" icon={DollarSign} label="Net Issued Life AP"
                  current={ap} threshold={next_tier.tier_ap}
                  streak={current_bonus > 0 ? 1 : 0}
                  qualMonths={1}
                  qualified={false}
                />
                <div
                  className="flex flex-col justify-center gap-2 rounded-2xl p-3.5"
                  style={{
                    background: 'rgba(255,255,255,0.7)',
                    border: '1px solid rgba(255,255,255,0.9)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7), 0 1px 3px rgba(11,20,55,0.04)',
                  }}
                  data-testid="bonus-marginal-card"
                >
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" style={{ color: SKY_DEEP }} />
                    <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: NAVY_SOFT }}>Marginal payout</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-['Manrope'] text-xl font-extrabold tabular-nums" style={{ color: NAVY }}>
                      +{fmtUSD(marginal_cash)}
                    </span>
                    <span className="text-[10px] tabular-nums" style={{ color: MUTED }}>
                      from {fmtUSD(gap_to_next)} more
                    </span>
                  </div>
                  <div className="text-[10px]" style={{ color: FAINT }}>
                    Clearing the next tier {below_floor ? 'unlocks' : 'adds'} {fmtUSD(marginal_cash)}.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {is_maxed && (
          <div className="border-t p-6 sm:p-8 text-center" style={{
            borderColor: 'rgba(229,235,245,0.7)',
            background: `linear-gradient(135deg, ${SKY}10 0%, ${SKY}20 100%)`,
          }}>
            <div className="flex items-center justify-center gap-2">
              <Sparkles className="h-5 w-5" style={{ color: SKY_DEEP }} />
              <span className="font-['Manrope'] text-lg font-bold" style={{ color: NAVY }}>Top tier reached — $25,000 locked.</span>
            </div>
          </div>
        )}
      </motion.div>

      {/* ---------- Tier ladder — same flat rung style as Promotions ---------- */}
      <div className="mt-6">
        <div className="mb-5 text-sm" style={{ color: MUTED }}>
          Highest tier wins — your bonus is the fixed cash for the top tier you reach, not the sum.
        </div>

        <div className="space-y-2">
          {ladderRendered.map((tier, idx) => {
            const isEarned = ap >= tier.tier_ap;
            const isCurrent = current_tier && tier.tier_ap === current_tier.tier_ap;
            const isNext = next_tier && tier.tier_ap === next_tier.tier_ap;
            const isAchieved = isEarned && !isCurrent;
            const isLocked = !isEarned && !isNext;

            let variant = 'locked';
            if (isCurrent) variant = 'current';
            else if (isAchieved) variant = 'achieved';
            else if (isNext) variant = 'next';

            let cardStyle = {
              background: 'rgba(255,255,255,0.5)',
              border: '1px solid rgba(229,235,245,0.7)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            };
            if (isCurrent) {
              cardStyle = {
                background: `linear-gradient(135deg, rgba(255,255,255,0.85) 0%, ${SKY}10 100%)`,
                border: `1px solid ${SKY}50`,
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                boxShadow: `0 8px 24px ${SKY}20, inset 0 1px 0 rgba(255,255,255,0.6)`,
              };
            } else if (isNext) {
              cardStyle = {
                background: 'rgba(255,255,255,0.7)',
                border: `1px solid ${SKY}40`,
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                boxShadow: `0 4px 12px rgba(11,20,55,0.04)`,
              };
            }

            return (
              <motion.div
                key={tier.tier_ap}
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.015 }}
                className="flex items-center gap-4 rounded-2xl px-4 py-3 transition-all hover:shadow-md"
                style={{ ...cardStyle, opacity: isLocked ? 0.7 : 1 }}
                data-testid={`bonus-tier-${tier.tier_ap}`}
              >
                <LevelBadge text={fmtUSDShort(tier.tier_ap)} variant={variant} size={48} />

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span
                      className="font-bold tracking-tight tabular-nums"
                      style={{
                        color: isLocked ? MUTED : NAVY,
                        fontSize: 'clamp(13px, 1.4vw, 15px)',
                      }}
                    >
                      {fmtUSD(tier.cash_bonus)} bonus
                    </span>
                    {isCurrent && (
                      <span
                        className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white"
                        style={{ background: `linear-gradient(90deg, ${SKY_DEEP}, ${SKY})` }}
                        data-testid="bonus-locked-tier"
                      >
                        Locked in
                      </span>
                    )}
                    {isNext && !isCurrent && (
                      <span className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest" style={{ background: '#E1F4FA', color: SKY_DEEP, border: `1px solid ${SKY}40` }}>
                        Next
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px]" style={{ color: isLocked ? FAINT : MUTED }}>
                    <span className="tabular-nums">Hit {fmtUSD(tier.tier_ap)} net life AP</span>
                  </div>
                </div>

                <div className="shrink-0">
                  {isAchieved ? <CheckCircle2 className="h-4 w-4" style={{ color: SKY_DEEP }} /> :
                   isLocked ? <Lock className="h-3.5 w-3.5" style={{ color: FAINT }} /> :
                   isNext ? <ChevronRight className="h-4 w-4" style={{ color: SKY }} /> : null}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Recent months strip */}
        {recent_months && recent_months.length > 0 && (
          <div className="mt-8">
            <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: FAINT }}>Recent months</div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              {[...recent_months].reverse().map((m) => (
                <div
                  key={m.label}
                  className="rounded-2xl p-3 backdrop-blur-md"
                  style={{
                    background: 'rgba(255,255,255,0.6)',
                    border: '1px solid rgba(229,235,245,0.7)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
                  }}
                  data-testid={`bonus-recent-${m.breeze_year}-${m.breeze_month}`}
                >
                  <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: FAINT }}>{m.label}</div>
                  <div className="mt-1.5 font-['Manrope'] text-xl font-extrabold tabular-nums" style={{ color: m.final_bonus_earned > 0 ? NAVY : MUTED }}>
                    {fmtUSD(m.final_bonus_earned)}
                  </div>
                  <div className="text-[10px] tabular-nums" style={{ color: MUTED }}>
                    {fmtUSDShort(m.net_ap)} AP
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 text-center text-[10px]" style={{ color: FAINT }}>
          Net issued LIFE AP only (IUL at target, no annuities) · Highest tier wins (not additive)
        </div>
      </div>
    </>
  );
}
